import { useState, useEffect } from "react";
import "../Leads.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSearchParams } from "react-router-dom";
import { DUVAL_NEW as columns, getErrorMessageText } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";
import { containsExcludedWord, formatTime } from "../../../utils/common";
import { Search, Trash2 } from "lucide-react";
import ConfirmModal from "../../../components/LogoutModal";
import Pagination from "../../../components/Pagination";

const DuvalNew = () => {
  const [fetchLoading, setFetchLoading] = useState(true);
  const [caseTypesLoading, setCaseTypesLoading] = useState(null);
  const [caseTypes, setCaseTypes] = useState([]); // eslint-disable-next-line
  const [caseType, setCaseType] = useState("");
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [leads, setLeads] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // const indexOfLastLead = currentPage * leadsPerPage;
  // const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  const fetchCases = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);
      setSearchParams({ page });
      // const offset = (page - 1) * limit;
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/duval_properties/cases?limit=${leadsPerPage}&page=${currentPage}&parties=true&attorneys=true&fees=true`);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.data || []).sort((a, b) => (b.id || 0) - (a.id || 0));
        setLeads(sortedLeads);
        setTotalLeads(data.total);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchCaseTypes = async () => {
    try {
      setCaseTypesLoading(true);
      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/duval_properties/case/types`);
      const data = await resp.data;
      if (data.success) {
        const sortedLeads = (data.data || []).sort((a, b) => b.category_name - a.category_name);
        setCaseTypes(sortedLeads);
      }
    } catch (err) {
      console.error("Failed to fetch Case Types", err);
    } finally {
      setCaseTypesLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseTypes(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchCases(currentPage, leadsPerPage, true);

    const interval = setInterval(() => {
      fetchCases(currentPage, leadsPerPage, false);
    }, 10000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteLead = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}api/duval_properties/case/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("❌ Failed to Delete Case");

      toast.success("Case Deleted successfully.");

      fetchCases(currentPage, leadsPerPage, true);
    } catch (err) {
      console.error("Error updating Case status:", err);
      toast.error(err.message || "Error Deleting Case.");
    }
  };

  const mapLeadData = (lead) => ({
    id: lead.id || "Not Found",
    created_at: formatTime(lead.created_at),
    file_date: lead.file_date,
    defendants: (
      <div className="flex flex-col gap-1 text-xs">
        {(lead.parties || [])
          .filter((p) => p.party_type === "DEFENDANT" && !containsExcludedWord(p.name))
          .sort((a, b) => (b.is_primary || 0) - (a.is_primary || 0))
          .map((p, i) => (
            <span key={p.id} className={p.is_primary ? "text-green-600 font-medium text-sm" : "text-gray-500"}>
              {p.name}
            </span>
          ))}
      </div>
    ),

    plaintiffs: (
      <div className="flex flex-col gap-1 text-xs">
        {(lead.parties || [])
          .filter((p) => p.party_type === "PLAINTIFF" && !containsExcludedWord(p.name))
          .sort((a, b) => (b.is_primary || 0) - (a.is_primary || 0))
          .map((p, i) => (
            <span key={p.id} className={p.is_primary ? "text-green-600 font-medium text-sm" : "text-gray-500"}>
              {p.name}
            </span>
          ))}
      </div>
    ),
    case_number: lead.case_number,
    case_status: lead.case_status,
    address: lead.address,
    street: lead.street,
    city: lead.city,
    state: lead.state,
    zip: lead.zip,
    case_type: lead.case_type,
    department: lead.department,
    division: lead.division,
    error_message: lead.is_loading ? <div>Loading ...</div> : <div>{getErrorMessageText(lead.error_message)}</div>,
  });

  return (
    <>
      <div>
        <div className="input_fields_container">
          <form className="w-full grid md:grid-cols-3 gap-4">
            <div className="!flex !items-center !justify-center md:col-span-3">
              <div className="flex space-x-2 w-full">
                <div className="input_field w-full">
                  <select name="property_type" disabled={caseTypesLoading} className={`cursor-pointer w-full focus:!outline-none text-gray-500`}>
                    <option value="">{caseTypesLoading ? "-- Loading Case Types --" : "-- Select Case Type --"}</option>
                    {caseTypes.map((c) => (
                      <option key={c.id} value={c.category_name}>
                        {c.category_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-32">
                  <button className="input_button text-nowrap" type="button" disabled={caseTypesLoading || !caseType}>
                    {caseTypesLoading ? "Loading..." : "Load Cases"}
                  </button>
                </div>
              </div>
              {/* <div className="!px-2 !pb-2">
                <ArrowBigDownDash size={24} className={`transition-transform duration-300 cursor-pointer flex justify-center item-center hover:text-blue-800 ${expandForm ? "rotate-180" : "rotate-0"}`} onClick={() => setExpandForm((prev) => !prev)} />
              </div> */}
            </div>
          </form>
        </div>
        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[97%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <a target="_blank" rel="noreferrer" href={`/property-search?address=${encodeURIComponent(lead["street"])}&city=${encodeURIComponent(lead["city"])}&state=${encodeURIComponent(lead["state"])}&zipcode=${encodeURIComponent(lead["zip"])}&lead-d=${encodeURIComponent(row["id"])}`} className="!text-blue-600 hover:!text-blue-800 flex items-center justify-center" title="Search Property">
                              <Search size={18} />
                            </a>
                          </td>
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button onClick={() => handleOpenDeleteModal(row)} className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer" title="Delete Lead">
                              <Trash2 size={18} />
                            </button>
                          </td>
                          {columns.map((col, idx) => (
                            <td
                              key={col.key || idx}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm text-black`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete this Case?`}
          isOpen={deleteModalOpen}
          onConfirmTitle="Delete"
          onConfirm={() => {
            if (selectedRow) {
              handleDeleteLead(selectedRow.id);
            }
            setDeleteModalOpen(false);
          }}
          onCancelTitle="Cancel"
          onCancel={() => setDeleteModalOpen(false)}
        />

        <Pagination totalItems={totalLeads} currentPage={currentPage} itemsPerPage={leadsPerPage} onPageChange={(page) => fetchCases(page)} />
      </div>
    </>
  );
};

export default DuvalNew;
